'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../../../components/badge';
import { Text } from '../../../../../components/styled/text';
import type { CardRenderContext } from '../../tool-config-types';
import {
  parseSignalData,
  parseSignalsListData,
  parseSignalsSearchData,
} from './SignalsRenderer';
import type { Signal } from './types';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { SignalStatusBadge } from '../../../../signals';

function parseSignalsOutput(output: unknown): {
  signals: Signal[];
  query?: string;
  totalResults?: number;
} {
  if (typeof output === 'object' && output !== null) {
    const searchData = parseSignalsSearchData(output);

    if (searchData) {
      return {
        signals: searchData.signals,
        query: searchData.query,
        totalResults: searchData.totalResults,
      };
    }

    const listData = parseSignalsListData(output);

    if (listData) {
      return {
        signals: listData,
      };
    }

    const oneSignal = parseSignalData(output as Partial<Signal>);

    if (oneSignal) {
      return {
        signals: [oneSignal],
      };
    }
  }

  return {
    signals: [],
  };
}

function SignalsFlatList({
  signals,
  onOpenObject,
}: {
  signals: Signal[];
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  return (
    <VStack alignItems="stretch" gap={0.5} width="100%" overflowX="hidden">
      {signals.map((signal, index) => {
        const signalId = signal.signalId ?? signal.id;
        const label =
          signal.title || signal.signalTitle || signal.description || 'Signal';
        const key = signalId ?? `${label}-${index}`;
        const canOpen = Boolean(onOpenObject && signalId);

        return (
          <HStack
            key={key}
            gap={1.5}
            py={1}
            px={1.5}
            alignItems="center"
            borderRadius="sm"
            cursor={canOpen ? 'pointer' : 'default'}
            _hover={canOpen ? { bg: 'bg.subtle' } : undefined}
            onClick={
              canOpen && signalId
                ? () =>
                    onOpenObject?.({
                      type: 'signal',
                      id: signalId as string,
                      label,
                    })
                : undefined
            }
          >
            {signal.method ? (
              <Badge size="sm" variant="outline">
                <Text fontSize="2xs" fontFamily="mono">
                  {signal.method}
                </Text>
              </Badge>
            ) : null}
            <Text fontSize="xs" color="fg.default" truncate flex={1}>
              {label}
            </Text>
            {signal.status ? (
              <SignalStatusBadge status={signal.status} />
            ) : null}
          </HStack>
        );
      })}
    </VStack>
  );
}

export function renderSignalsCard(ctx: CardRenderContext) {
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const parsedOutput = parseSignalsOutput(ctx.toolCallData.output);
  const signals = parsedOutput.signals;
  const totalResults = parsedOutput.totalResults ?? signals.length;

  if (
    !isRunning &&
    signals.length === 0 &&
    !ctx.toolCallData.error &&
    ctx.toolCallData.status === 'completed'
  ) {
    return null;
  }

  const headerMeta =
    signals.length > 0 || parsedOutput.query ? (
      <HStack gap={1} flexShrink={0} minWidth={0}>
        <Text fontSize="2xs" color="fg.muted" flexShrink={0}>
          {totalResults}
        </Text>
        {parsedOutput.query ? (
          <Text fontSize="2xs" color="fg.muted" truncate maxW="180px">
            {parsedOutput.query}
          </Text>
        ) : null}
      </HStack>
    ) : undefined;

  return (
    <InlineExecutionToolCard
      title={ctx.toolName}
      icon={ctx.toolIcon}
      status={ctx.toolCallData.status}
      error={ctx.toolCallData.error}
      defaultExpanded={false}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
      headerMeta={headerMeta}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    >
      {signals.length > 0 ? (
        <SignalsFlatList signals={signals} onOpenObject={ctx.onOpenObject} />
      ) : null}
    </InlineExecutionToolCard>
  );
}
